import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { setModal, selectModal } from "../../../redux/modalSlice";
import { useForm, Controller } from 'react-hook-form'
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import { useNavigate } from 'react-router-dom'
import Loading from "../../../components/Loading";

export default function CustomerSearchModal() {
  const dispatch = useDispatch();
  const navi = useNavigate()
  const modal = useSelector(selectModal);
  const { handleSubmit, control, reset } = useForm();
  const request = useRequest()
  const customerId = modal.options?.customerId
  const [saving, setSaving] = useState(false)
  const [duplicateData, setDuplicateData] = useState(null)
  const [isLoaded, setIsLoaded] = useState(false)

  const active = modal.is_active && modal.type === 'customer_search_modal'
  const inFlightCustomerId = useRef(null)

  const resetState = () => {
    setSaving(false)
    setDuplicateData(null)
    setIsLoaded(false)
    reset()
  }

  const handleCancel = () => {
    inFlightCustomerId.current = null
    resetState()
    dispatch(setModal({ is_active: false }));
  };

  // This modal stays mounted in the Footer; clear the previous customer's duplicate banners
  // before loading this customer's so they can't be shown (or linked to) on the wrong page.
  useEffect(() => {
    if (active && customerId) {
      resetState()
      getDuplicateCustomer(customerId)
    }
  }, [active, customerId])

  const getDuplicateCustomer = async (forCustomerId) => {
    inFlightCustomerId.current = forCustomerId
    setIsLoaded(false)
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${forCustomerId}/banner`)
    try {
      const response = await request.get()
      // Ignore a response that a close or a newer opening has superseded.
      if (inFlightCustomerId.current !== forCustomerId) return
      if (response.status === 200 || response.status === 201) {
        setDuplicateData(response?.data?.data?.duplicate_banners ?? [])
      } else {
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
      setIsLoaded(true)
    } catch (error) {
      if (inFlightCustomerId.current !== forCustomerId) return
      setIsLoaded(true)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  const onSubmit = async (formData) => {
    setSaving(true)
    let sourceCustomerId = customerId
    let targetCustomerId = ""
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${sourceCustomerId}/check_merge/${formData.target_customer_guid}`);
    try {
      const response = await request.get().then((response) => response.data.data);
      if (response?.id) {
        setSaving(false)
        targetCustomerId = response?.id
        handleCancel()
        navi(`/customer_merge_tool?scid=${sourceCustomerId}&tcid=${targetCustomerId}`)
      } else {
        setSaving(false)
        dispatch(setNotification({ type: 'error', body: "No customer found" }))
      }
    } catch (error) {
      setSaving(false)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  // eslint-disable-next-line no-nested-ternary
  return active ? (
    <form onSubmit={handleSubmit(onSubmit)} id="customer_search_modal_form">
      <div className="modal is-active">
        <div className="modal-background"></div>
        <div className="modal-card column is-3">
          <header className="modal-card-head py-3">
            <p className="modal-card-title is-capitalized">
              Customer Search
            </p>
            <button className="delete" aria-label="close" onClick={handleCancel}></button>
          </header>
          <section
            className="modal-card-body modal-card-body pt-4 pb-0"
            style={{ minHeight: "50px", overflow: 'hidden' }}
          >
            {isLoaded ? (
              <>
                {duplicateData?.map((item) => {
                  return (
                    <div key={`${item?.customer?.id}-${item?.message}`} className={`columns mb-0 is-flex-direction-column mt-3 notification is-warning is-light p-0`}>
                      <div className="column">
                        <h3>{ item?.message }</h3>
                        <h3>Customer GUID: <a href={`/customer/${item?.customer?.id}`}> { item?.customer?.guid } </a></h3>
                      </div>
                    </div>
                  )
                })}
                <div className="columns mt-3 mb-0 is-flex-direction-column">
                  <div className="column">
                    <div className="field is-horizontal dmc-modal-field">
                      <label className={`dmc-modal-label`} htmlFor="address2">Customer GUID:</label>
                      <div className="control">
                        <Controller
                          name="target_customer_guid"
                          control={control}
                          defaultValue=""
                          rules={{ required: true }}
                          render={({ field }) => (
                            <input {...field} type="text" className="input is-small" placeholder="Please enter GUID" />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : <Loading />}
          </section>
          <footer className="modal-card-foot is-justify-content-end py-1">
            <div className="lox-form__buttons-wrapper has-text-right">
              <button type="button" className="button is-small has-background-grey has-text-white" onClick={handleCancel}>Cancel</button>
              <button type="submit" className="button is-link is-small" disabled={saving && 'disabled'}>{saving ? 'Searching' : 'Merge'}</button>
            </div>
          </footer>
        </div>
      </div>
    </form>
  ) : null;
}
